import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  Mic,
  Square,
  Upload,
  FileAudio,
  Play,
  Pause,
  Trash2,
  CheckCircle2,
  X,
  AlertCircle,
  Loader2,
  ClipboardCopy,
  ChevronDown,
  Activity,
  Heart,
  Calendar,
  Layers,
  FileText,
  Headphones
} from "lucide-react";
import axios from "axios";
import { BASE_URL } from "../interface/Config";
import { apiHandler } from "../../services/authService";

import { createPortal } from "react-dom";
import SnackbarMessage from "../SnackBAr/SnackbarNotification";

interface Props {
  patientId?: string;
  hideGlobalTrigger?: boolean;
}

interface SoapResponse {
  transcript: string;
  clean_conversation: string;
  soap: {
    subjective?: any;
    patient_history?: any;
    objective?: {
      vitals?: {
        heart_rate?: string;
        bp?: string;
        temp?: string;
        rr?: string;
        spo2?: string;
      };
      [key: string]: any;
    };
    assessment?: any;
    plan?: any;
    follow_up?: any;
    ros?: any;
    extracted_diagnosis?: string[];
    extracted_symptoms?: string[];
  };
  billing?: any;
  insights?: {
    symptoms?: string[];
    diagnoses?: string[];
    problem?: string;
  };
  status: string;
}

export const AmbientAssistant: React.FC<Props> = ({ patientId, hideGlobalTrigger = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SoapResponse | null>(null);
  const [activeTab, setActiveTab] = useState<"soap" | "transcript" | "insights">("soap");
  const [expandedSection, setExpandedSection] = useState<string | null>("subjective");
  const [mounted, setMounted] = useState(false);
  const [isCallActive, setIsCallActive] = useState(() => {
    return sessionStorage.getItem('isCallActive') === 'true';
  });

  // Patient context state
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [selectedPatientName, setSelectedPatientName] = useState<string | null>(null);
  const [savingSoap, setSavingSoap] = useState<boolean>(false);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [editableSoap, setEditableSoap] = useState<any>(null);
  const [bgSaving, setBgSaving] = useState<boolean>(false);
  const [bgSaveMessage, setBgSaveMessage] = useState<string>("");
  const [bgSaveSeverity, setBgSaveSeverity] = useState<'success' | 'info' | 'warning' | 'error'>("info");

  // Sync editableSoap with result changes
  useEffect(() => {
    if (result && result.soap) {
      setEditableSoap(JSON.parse(JSON.stringify(result.soap)));
    } else {
      setEditableSoap(null);
    }
  }, [result]);

  // Dispatch toggle event to communicate with video call window & warm up Render backend
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('ambient-ai-toggled', { detail: { open: isOpen } }));
    if (isOpen) {
      axios.get("https://ambient-ai-5039.onrender.com/").catch(() => {
        // Ignore error - we just want to ping the domain to wake it up from cold sleep
      });
    }
  }, [isOpen]);



  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);

  // Stop timer, set mounted, and register card triggers
  useEffect(() => {
    setMounted(true);

    const handleOpenExternal = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        const { patientId: pId, patientName: pName } = customEvent.detail;
        setSelectedPatientId(pId || null);
        setSelectedPatientName(pName || null);
        setIsOpen(true);
        
        // Reset console state to prepare for new consultation
        setResult(null);
        setError(null);
        setAudioFile(null);
        setAudioUrl(null);
      }
    };

    const handleCallStatus = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        setIsCallActive(customEvent.detail.active);
      }
    };

    window.addEventListener('open-ambient-ai', handleOpenExternal);
    window.addEventListener('in-app-call-status', handleCallStatus);

    return () => {
      setMounted(false);
      window.removeEventListener('open-ambient-ai', handleOpenExternal);
      window.removeEventListener('in-app-call-status', handleCallStatus);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Format recording timer: mm:ss
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Start audio recording
  const startRecording = async () => {
    setError(null);
    setAudioFile(null);
    setAudioUrl(null);
    audioChunksRef.current = [];

    try {
      // 1. Capture local doctor microphone
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const options = MediaRecorder.isTypeSupported("audio/webm")
        ? { mimeType: "audio/webm", audioBitsPerSecond: 64000 }
        : { audioBitsPerSecond: 64000 };

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const file = new File([audioBlob], `consult_record_${Date.now()}.webm`, {
          type: "audio/webm",
        });
        setAudioFile(file);
        setAudioUrl(URL.createObjectURL(audioBlob));
        
        // Stop stream tracks
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start(250); // Slice data every 250ms
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = window.setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (err: any) {
      console.error("Recording error:", err);
      setError("Microphone access denied or not supported in this browser.");
    }
  };

  // Stop audio recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  // Auto-stop recording if limit reached (10 minutes = 600 seconds)
  useEffect(() => {
    if (isRecording && recordingTime >= 600) {
      stopRecording();
      setError("Recording limit of 10 minutes reached. Recording stopped automatically.");
    }
  }, [recordingTime, isRecording]);

  // Handle local file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setError(null);
      setAudioFile(file);
      setAudioUrl(URL.createObjectURL(file));
      setResult(null);
    }
  };

  // Reset file/recording
  const handleReset = () => {
    setAudioFile(null);
    setAudioUrl(null);
    setResult(null);
    setError(null);
  };

  // Submit to Ambient AI API
  const handleSubmit = async () => {
    if (!audioFile) {
      setError("Please record a consultation or upload an audio file first.");
      return;
    }

    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append("file", audioFile);

    try {
      const response = await axios.post(
        "https://ambient-ai-5039.onrender.com/api/v1/ai/voice-to-soap",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data && response.data.status === "success") {
        setResult(response.data);
        setActiveTab("soap");
      } else {
        throw new Error(response.data?.message || "Ambient parsing failed.");
      }
    } catch (err: any) {
      console.error("API Call error:", err);
      setError(
        err?.response?.data?.message ||
          "Failed to connect to Ambient AI service. Please verify server status."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSoapToChart = async () => {
    const activePatientId = patientId || selectedPatientId;
    if (!activePatientId) {
      setError("No active patient context selected. Please select a patient.");
      return;
    }
    const activeSoap = editableSoap || (result ? result.soap : null);
    if (!activeSoap) return;

    try {
      setSavingSoap(true);
      setError(null);
      
      // Close drawer immediately so it doesn't disturb the consult UI
      setIsOpen(false);
      setBgSaving(true);
      setBgSaveMessage("Saving SOAP notes to chart in background...");
      setBgSaveSeverity("info");

      const token = sessionStorage.getItem("authToken") || "";
      const currentDate = new Date().toISOString().split("T")[0];
      const currentTime = new Date().toTimeString().split(" ")[0].slice(0, 5); // "HH:MM"

      // 1. Save SOAP Notes narrative blob to docs (to avoid masters slug constraint)
      // try {
      //   const soapDocPayload = {
      //     user_id: activePatientId,
      //     slug: "notes",
      //     document_source: "notes",
      //     addition_info: {
      //       date: currentDate,
      //       title: "Ambient AI Complete SOAP Record",
      //       notes: JSON.stringify(activeSoap)
      //     }
      //   };
      //   const docsUrl = `${BASE_URL}/resource/docs`;
      //   await apiHandler(token, "POST", soapDocPayload, docsUrl);
      // } catch (e) {
      //   console.error("Failed to save complete SOAP narrative to docs:", e);
      // }

      // 2. Save each Extracted Diagnosis into docs with slug "icd"
      if (activeSoap.extracted_diagnosis && activeSoap.extracted_diagnosis.length > 0) {
        for (const diag of activeSoap.extracted_diagnosis) {
          try {
            const docPayload = {
              slug: "icd",
              user_id: activePatientId,
              addition_info: {
                date: currentDate,
                title: "Ambient Diagnosis",
                notes: typeof diag === 'object' && diag !== null ? (diag.name || diag.diagnosis || diag.title || Object.values(diag).find(v => typeof v === 'string') || 'Unknown') : String(diag)
              }
            };
            const docsUrl = `${BASE_URL}/resource/docs`;
            await apiHandler(token, "POST", docPayload, docsUrl);
          } catch (e) {
            console.error("Failed to save diagnosis doc:", e);
          }
        }
      }

      // 3. Save Symptoms into docs with slug "chief-complaints"
      if (activeSoap.extracted_symptoms && activeSoap.extracted_symptoms.length > 0) {
        for (const symptom of activeSoap.extracted_symptoms) {
          try {
            const complaintPayload = {
              slug: "chief-complaints",
              user_id: activePatientId,
              addition_info: {
                date: currentDate,
                title: typeof symptom === 'object' && symptom !== null ? (symptom.name || symptom.symptom || symptom.title || Object.values(symptom).find(v => typeof v === 'string') || 'Unknown') : String(symptom),
                notes: "Extracted via Telegaruda Ambient AI Voice engine."
              }
            };
            const docsUrl = `${BASE_URL}/resource/docs`;
            await apiHandler(token, "POST", complaintPayload, docsUrl);
          } catch (e) {
            console.error("Failed to save symptom:", e);
          }
        }
      }

      // 4. Save parsed Vitals into /resource/vitals
      const vitalsObj = activeSoap.objective?.vitals || {};

      // 4a. Blood Pressure (systolic_bp, diastolic_bp, pulse)
      let bpSystolic = Number(vitalsObj.systolic_bp) || null;
      let bpDiastolic = Number(vitalsObj.diastolic_bp) || null;
      let bpPulse = Number(vitalsObj.heart_rate) || Number(vitalsObj.pulse) || null;

      // Extract BP from physical exam or text if not explicitly in vitalsObj
      if (!bpSystolic && !bpDiastolic) {
        const textToCheck = String(vitalsObj.blood_pressure || vitalsObj.bp || activeSoap.objective?.physical_exam || "");
        const match = textToCheck.match(/(\d{2,3})\s*\/\s*(\d{2,3})/);
        if (match) {
          bpSystolic = Number(match[1]);
          bpDiastolic = Number(match[2]);
        } else {
          const singleMatch = textToCheck.match(/(\d{2,3})/);
          if (singleMatch) {
            bpSystolic = Number(singleMatch[1]);
          }
        }
      }

      if (bpSystolic) {
        try {
          const bpPayload = {
            slug: "blood-pressure",
            user_id: activePatientId,
            details: {
              date: currentDate,
              time: currentTime,
              systolic: bpSystolic,
              diastolic: bpDiastolic || 0,
              pulse: bpPulse || 72,
              bpFlag: bpSystolic > 130 || (bpDiastolic && bpDiastolic > 85) ? "High" : bpSystolic < 90 || (bpDiastolic && bpDiastolic < 60) ? "Low" : "Normal",
              bpFlagColor: bpSystolic > 130 || (bpDiastolic && bpDiastolic > 85) ? "#FF0000" : bpSystolic < 90 || (bpDiastolic && bpDiastolic < 60) ? "#FFC107" : "#008000"
            }
          };
          const vitalsUrl = `${BASE_URL}/resource/vitals`;
          await apiHandler(token, "POST", bpPayload, vitalsUrl);
        } catch (e) {
          console.error("Error saving BP vital record:", e);
        }
      }

      // 4b. Temperature
      let tempVal = Number(vitalsObj.temperature) || null;
      if (!tempVal) {
        const textToCheck = String(activeSoap.objective?.physical_exam || "");
        const match = textToCheck.match(/(\d{2,3}(?:\.\d+)?)\s*(?:°|deg)?\s*[FfC]/);
        if (match) {
          tempVal = Number(match[1]);
        }
      }

      if (tempVal) {
        try {
          const tempPayload = {
            slug: "temperature",
            user_id: activePatientId,
            details: {
              date: currentDate,
              time: currentTime,
              type: "Oral",
              unit: "°F",
              temperature: tempVal,
              temperatureFlag: tempVal > 99 ? "High" : tempVal < 96 ? "Low" : "Normal",
              temperatureFlagColor: tempVal > 99 ? "#FF0000" : tempVal < 96 ? "#FFC107" : "#008000"
            }
          };
          const vitalsUrl = `${BASE_URL}/resource/vitals`;
          await apiHandler(token, "POST", tempPayload, vitalsUrl);
        } catch (e) {
          console.error("Error saving Temperature vital record:", e);
        }
      }

      // 4c. SpO2
      let spo2Val = Number(vitalsObj.spo2) || Number(vitalsObj.oxygen_saturation) || null;
      if (!spo2Val) {
        const textToCheck = String(activeSoap.objective?.physical_exam || "");
        const match = textToCheck.match(/(?:spo2|pulse\s*ox|oxygen\s*sat|saturation)\s*(?:level\s*)?of\s*(\d{2,3})/i) || textToCheck.match(/(\d{2,3})\s*%\s*(?:spo2|oxygen)/i);
        if (match) {
          spo2Val = Number(match[1]);
        }
      }

      if (spo2Val) {
        try {
          const spo2Payload = {
            slug: "spO2",
            user_id: activePatientId,
            details: {
              date: currentDate,
              time: currentTime,
              spo2: spo2Val,
              unit: "%",
              pulse: bpPulse || 72,
              spo2Flag: spo2Val < 95 ? "Low" : "Normal",
              spo2FlagColor: spo2Val < 95 ? "#FF0000" : "#008000"
            }
          };
          const vitalsUrl = `${BASE_URL}/resource/vitals`;
          await apiHandler(token, "POST", spo2Payload, vitalsUrl);
        } catch (e) {
          console.error("Error saving SpO2 vital record:", e);
        }
      }

      // 4d. Heart Rate
      let hrVal = bpPulse || Number(vitalsObj.heart_rate) || Number(vitalsObj.pulse) || null;
      if (hrVal) {
        try {
          const hrPayload = {
            slug: "heart-rate",
            user_id: activePatientId,
            details: {
              date: currentDate,
              time: currentTime,
              type: "Manual",
              heart: hrVal,
              interpretation: hrVal > 100 ? "Tachycardia" : hrVal < 60 ? "Bradycardia" : "Normal",
              heartRateFlag: hrVal > 100 || hrVal < 60 ? "Abnormal" : "Normal",
              heartRateFlagColor: hrVal > 100 || hrVal < 60 ? "#FF0000" : "#008000"
            }
          };
          const vitalsUrl = `${BASE_URL}/resource/vitals`;
          await apiHandler(token, "POST", hrPayload, vitalsUrl);
        } catch (e) {
          console.error("Error saving Heart Rate vital record:", e);
        }
      }

      // 4e. Respiration Rate
      let rrVal = Number(vitalsObj.respiratory_rate) || Number(vitalsObj.respiration_rate) || null;
      if (rrVal) {
        try {
          const rrPayload = {
            slug: "respiration",
            user_id: activePatientId,
            details: {
              date: currentDate,
              time: currentTime,
              respiration: rrVal,
              respirationFlag: rrVal > 20 || rrVal < 12 ? "Abnormal" : "Normal",
              respirationFlagColor: rrVal > 20 || rrVal < 12 ? "#FF0000" : "#008000"
            }
          };
          const vitalsUrl = `${BASE_URL}/resource/vitals`;
          await apiHandler(token, "POST", rrPayload, vitalsUrl);
        } catch (e) {
          console.error("Error saving Respiration Rate vital record:", e);
        }
      }

      // 4f. BMI
      let heightVal = Number(vitalsObj.height) || null;
      let weightVal = Number(vitalsObj.weight) || null;
      let bmiVal = Number(vitalsObj.bmi) || null;

      if (heightVal && weightVal && !bmiVal) {
        const heightMeters = heightVal / 100;
        bmiVal = Number((weightVal / (heightMeters * heightMeters)).toFixed(2));
      }

      if (bmiVal && heightVal && weightVal) {
        try {
          const bmiPayload = {
            slug: "bmi",
            user_id: activePatientId,
            details: {
              date: currentDate,
              time: currentTime,
              height: heightVal,
              weight: weightVal,
              bmi: bmiVal,
              height_unit: "cm",
              weight_unit: "kg",
              bmiFlag: bmiVal > 25 ? "Overweight" : bmiVal < 18.5 ? "Underweight" : "Normal",
              bmiFlagColor: bmiVal > 25 ? "#FF0000" : bmiVal < 18.5 ? "#FFC107" : "#008000"
            }
          };
          const vitalsUrl = `${BASE_URL}/resource/vitals`;
          await apiHandler(token, "POST", bmiPayload, vitalsUrl);
        } catch (e) {
          console.error("Error saving BMI vital record:", e);
        }
      }

      // 4g. Blood Sugar
      let bloodSugarVal = Number(vitalsObj.blood_sugar) || null;
      if (bloodSugarVal) {
        try {
          const bsPayload = {
            slug: "blood-sugar",
            user_id: activePatientId,
            details: {
              date: currentDate,
              time: currentTime,
              blood_sugar: bloodSugarVal,
              type: "Random",
              unit: "mg/dL"
            }
          };
          await apiHandler(token, "POST", bsPayload, `${BASE_URL}/resource/vitals`);
        } catch (e) {
          console.error("Error saving Blood Sugar vital record:", e);
        }
      }

      // 4g. Expanded Vitals
      const expandedVitals = [
        { slug: "hemoglobin", key: "hemoglobin", val: Number(vitalsObj.hemoglobin) },
        { slug: "hct", key: "hct", val: Number(vitalsObj.hct) || Number(vitalsObj.hematocrit) },
        { slug: "uric_acid", key: "uric_acid", val: Number(vitalsObj.bloodUricAcid) || Number(vitalsObj.uric_acid) },
        { slug: "keytone", key: "keytone", val: Number(vitalsObj.bloodKetone) || Number(vitalsObj.ketone) },
        { slug: "urea", key: "urea", val: Number(vitalsObj.urea) },
        { slug: "creatinine", key: "creatinine", val: Number(vitalsObj.creatinine) },
        { slug: "gfr", key: "gfr", val: Number(vitalsObj.gfr) }
      ];

      for (const vital of expandedVitals) {
        if (vital.val) {
          try {
            const payload = { slug: vital.slug, user_id: activePatientId, details: { date: currentDate, time: currentTime, [vital.key]: vital.val } };
            if (vital.slug === "gfr") Object.assign(payload.details, { unit: 'mL/min/1.73 m²' });
            await apiHandler(token, "POST", payload, `${BASE_URL}/resource/vitals`);
          } catch (e) { console.error(`Error saving ${vital.slug}:`, e); }
        }
      }

      // 4h. Lipid Profile
      let hdlVal = Number(vitalsObj.hdl) || 0;
      let ldlVal = Number(vitalsObj.ldl) || 0;
      let totalChol = Number(vitalsObj.total_cholesterol) || Number(vitalsObj.cholesterol) || 0;
      let triglycerides = Number(vitalsObj.triglycerides) || 0;
      if (hdlVal || ldlVal || totalChol || triglycerides) {
        try {
          const lipidPayload = {
            slug: "lipid-profile",
            user_id: activePatientId,
            details: {
              date: currentDate,
              time: currentTime,
              hdl: hdlVal,
              ldl: ldlVal,
              vldl: vitalsObj.vldl || 0,
              total: totalChol,
              triglycerides: triglycerides,
              hdl_ldl: hdlVal && ldlVal ? Number((ldlVal / hdlVal).toFixed(2)) : 0,
              ldl_unit: 'mg/dL', hdl_unit: 'mg/dL', vldl_unit: 'mg/dL', total_unit: 'mg/dL', triglycerides_unit: 'mg/dL'
            }
          };
          await apiHandler(token, "POST", lipidPayload, `${BASE_URL}/resource/vitals`);
        } catch (e) { console.error("Error saving Lipid Profile:", e); }
      }

      // 5. Save Subjective details to /resource/patientHistories
      const subjectiveObj = activeSoap.subjective || {};

      // 5a. Chief Complaint (directly as a single summary chief complaint doc)
      const chiefComplaintStr = subjectiveObj.chief_complaint;
      if (chiefComplaintStr && typeof chiefComplaintStr === "string" && chiefComplaintStr.trim() !== "") {
        try {
          const complaintPayload = {
            slug: "chief-complaints",
            user_id: activePatientId,
            addition_info: {
              date: currentDate,
              title: chiefComplaintStr,
              notes: "Extracted via Telegaruda Ambient AI Voice engine."
            }
          };
          const docsUrl = `${BASE_URL}/resource/docs`;
          await apiHandler(token, "POST", complaintPayload, docsUrl);
        } catch (e) {
          console.error("Error saving chief complaint subjective record:", e);
        }
      }

      // 5b. Allergies
      const allergyInput = subjectiveObj.allergies || activeSoap.patient_history?.allergies;
      if (allergyInput && (typeof allergyInput === "string" || Array.isArray(allergyInput))) {
        let allergyItems: string[] = [];
        if (Array.isArray(allergyInput)) {
          allergyItems = allergyInput.map(s => String(s || "").trim()).filter(s => s && s.toLowerCase() !== "none" && s.toLowerCase() !== "none reported");
        } else if (typeof allergyInput === "string" && allergyInput.toLowerCase() !== "none" && allergyInput.toLowerCase() !== "none reported" && allergyInput.trim() !== "") {
          allergyItems = [allergyInput];
        }

        for (const allergyStr of allergyItems) {
          try {
            const allergyPayload = {
              patient_id: activePatientId,
              slug: "allergy",
              values: {
                date: currentDate,
                name: {
                  name: allergyStr,
                  attributes: {
                    allergy_type: "Allergy",
                    allergy_category: "Active"
                  },
                  master_type_slug: "allergy_names"
                },
                reaction: {
                  name: "Mild Reaction",
                  master_type_slug: "reactions"
                },
                severity: "Mild",
                is_active: 1
              }
            };
            const historiesUrl = `${BASE_URL}/resource/patientHistories`;
            await apiHandler(token, "POST", allergyPayload, historiesUrl);
          } catch (e) {
            console.error("Error saving allergy subjective record:", e);
          }
        }
      }

      // 5c. Medical History
      const medHistoryInput = subjectiveObj.past_medical_history || subjectiveObj.medical_history || activeSoap.patient_history?.past_medical_history || activeSoap.patient_history?.medical_history;
      if (medHistoryInput && (typeof medHistoryInput === "string" || Array.isArray(medHistoryInput))) {
        let medHistoryItems: string[] = [];
        if (Array.isArray(medHistoryInput)) {
          medHistoryItems = medHistoryInput.map(s => String(s || "").trim()).filter(s => s && s.toLowerCase() !== "none");
        } else if (typeof medHistoryInput === "string" && medHistoryInput.toLowerCase() !== "none" && medHistoryInput.trim() !== "") {
          medHistoryItems = [medHistoryInput];
        }

        for (const medHistoryStr of medHistoryItems) {
          try {
            const medHistoryPayload = {
              patient_id: activePatientId,
              slug: "medical-history",
              values: {
                date: currentDate,
                condition: {
                  name: medHistoryStr,
                  master_type_slug: "conditions"
                },
                diagnosed_date: currentDate,
                status: "Active"
              }
            };
            const historiesUrl = `${BASE_URL}/resource/patientHistories`;
            await apiHandler(token, "POST", medHistoryPayload, historiesUrl);
          } catch (e) {
            console.error("Error saving medical history subjective record:", e);
          }
        }
      }

      // 5d. Surgical History
      const surgHistoryInput = subjectiveObj.surgical_history || activeSoap.patient_history?.surgical_history;
      if (surgHistoryInput && (typeof surgHistoryInput === "string" || Array.isArray(surgHistoryInput))) {
        let surgHistoryItems: string[] = [];
        if (Array.isArray(surgHistoryInput)) {
          surgHistoryItems = surgHistoryInput.map(s => String(s || "").trim()).filter(s => s && s.toLowerCase() !== "none");
        } else if (typeof surgHistoryInput === "string" && surgHistoryInput.toLowerCase() !== "none" && surgHistoryInput.trim() !== "") {
          surgHistoryItems = [surgHistoryInput];
        }

        for (const surgHistoryStr of surgHistoryItems) {
          try {
            const surgHistoryPayload = {
              patient_id: activePatientId,
              slug: "surgical-history",
              values: {
                date: currentDate,
                surgical_name: {
                  name: surgHistoryStr,
                  master_type_slug: "surgeries"
                },
                surgery_date: currentDate,
                surgery_status: "Completed"
              }
            };
            const historiesUrl = `${BASE_URL}/resource/patientHistories`;
            await apiHandler(token, "POST", surgHistoryPayload, historiesUrl);
          } catch (e) {
            console.error("Error saving surgical history subjective record:", e);
          }
        }
      }

      // 5e. Social History
      const socialHistoryInput = subjectiveObj.social_history || activeSoap.patient_history?.social_history;
      if (socialHistoryInput) {
        try {
          let tobaccoUse = "No";
          let alcoholUse = "No";
          let drugUse = "No";
          let notes = "";

          if (typeof socialHistoryInput === "string" && socialHistoryInput.toLowerCase() !== "none" && socialHistoryInput.trim() !== "") {
            tobaccoUse = socialHistoryInput.toLowerCase().includes("smok") || socialHistoryInput.toLowerCase().includes("tobacco") ? "Yes" : "No";
            alcoholUse = socialHistoryInput.toLowerCase().includes("drink") || socialHistoryInput.toLowerCase().includes("alcohol") ? "Yes" : "No";
            drugUse = socialHistoryInput.toLowerCase().includes("drug") || socialHistoryInput.toLowerCase().includes("substance") || socialHistoryInput.toLowerCase().includes("cocaine") || socialHistoryInput.toLowerCase().includes("marijuana") ? "Yes" : "No";
            notes = socialHistoryInput;
          } else if (typeof socialHistoryInput === "object" && socialHistoryInput !== null) {
            const smoking = String(socialHistoryInput.smoking || "").toLowerCase();
            const alcohol = String(socialHistoryInput.alcohol || "").toLowerCase();
            const drugs = String(socialHistoryInput.drugs || socialHistoryInput.drug || "").toLowerCase();
            const diet = String(socialHistoryInput.diet || "");
            const activity = String(socialHistoryInput.physical_activity || "");
            
            tobaccoUse = smoking.includes("yes") || smoking.includes("smok") || smoking.includes("tobacco") || smoking.includes("current") ? "Yes" : "No";
            alcoholUse = alcohol.includes("yes") || alcohol.includes("drink") || alcohol.includes("alcohol") || alcohol.includes("current") ? "Yes" : "No";
            drugUse = drugs.includes("yes") || drugs.includes("use") || drugs.includes("current") || drugs.includes("drug") ? "Yes" : "No";
            
            const parts = [];
            if (socialHistoryInput.smoking) parts.push(`Smoking: ${socialHistoryInput.smoking}`);
            if (socialHistoryInput.alcohol) parts.push(`Alcohol: ${socialHistoryInput.alcohol}`);
            if (socialHistoryInput.drugs || socialHistoryInput.drug) parts.push(`Drugs: ${socialHistoryInput.drugs || socialHistoryInput.drug}`);
            if (diet) parts.push(`Diet: ${diet}`);
            if (activity) parts.push(`Physical Activity: ${activity}`);
            notes = parts.join(", ") || "No significant social history.";
          }

          if (notes) {
            // Check if there is an existing social-history record to PATCH
            let existingHistoryId: number | null = null;
            try {
              const getUrl = `${BASE_URL}/resource/patientHistories?limit=10&page=1&from=&to=&order_by=id&dir=2&user_id=${activePatientId}&slug=social-history&searchkey=undefined`;
              const getResponse = await apiHandler(token, "GET", null, getUrl);
              if (getResponse && getResponse.data && getResponse.data.data && getResponse.data.data.patient_histories) {
                const histories = getResponse.data.data.patient_histories;
                if (histories.length > 0) {
                  existingHistoryId = histories[0].id;
                }
              }
            } catch (getErr) {
              console.error("Error fetching existing social history:", getErr);
            }

            const socialHistoryPayload = {
              patient_id: activePatientId,
              slug: "social-history",
              values: {
                date: currentDate,
                tobacco: tobaccoUse === "Yes",
                alcohol: alcoholUse === "Yes",
                drug: drugUse === "Yes",
                tobacco_use: tobaccoUse,
                alcohol_use: alcoholUse,
                drug_use: drugUse,
                notes: [notes],
                additional_info: {
                  smoking: typeof socialHistoryInput === "object" && socialHistoryInput?.smoking ? String(socialHistoryInput.smoking) : (tobaccoUse === "Yes" ? notes : ""),
                  alcohol: typeof socialHistoryInput === "object" && socialHistoryInput?.alcohol ? String(socialHistoryInput.alcohol) : (alcoholUse === "Yes" ? notes : ""),
                  drugs: typeof socialHistoryInput === "object" && (socialHistoryInput?.drugs || socialHistoryInput?.drug) ? String(socialHistoryInput.drugs || socialHistoryInput.drug) : (drugUse === "Yes" ? notes : "")
                }
              }
            };

            if (existingHistoryId) {
              const historiesUrl = `${BASE_URL}/resource/patientHistories/${existingHistoryId}`;
              await apiHandler(token, "PATCH", socialHistoryPayload, historiesUrl);
            } else {
              const historiesUrl = `${BASE_URL}/resource/patientHistories`;
              await apiHandler(token, "POST", socialHistoryPayload, historiesUrl);
            }
          }
        } catch (e) {
          console.error("Error saving social history subjective record:", e);
        }
      }

      // 5f. Family History
      const familyHistoryInput = subjectiveObj.family_history || activeSoap.patient_history?.family_history;
      if (familyHistoryInput && (typeof familyHistoryInput === "string" || Array.isArray(familyHistoryInput))) {
        try {
          // Local static list of family history masters to avoid permission issues with masters endpoint
          const masterDiseases = [
            { name: "Alcohol or Drug Abuse", slug: "alcohol-or-drug-abuse" },
            { name: "Not Alive", slug: "alive" },
            { name: "Allergies", slug: "allergies" },
            { name: "Anesthesia", slug: "anesthesia" },
            { name: "Anxiety,Depression or Psychiatric Illness", slug: "anxietydepression-or-psychiatric-illness" },
            { name: "Asthma", slug: "asthma" },
            { name: "Cancer", slug: "cancer" },
            { name: "Cystic Fibrosis", slug: "cystic-fibrosis" },
            { name: "Diabetes", slug: "diabetes" },
            { name: "Eczema", slug: "eczema" },
            { name: "Emphysema", slug: "emphysema" },
            { name: "Genetic Disorder", slug: "genetic-disorder" },
            { name: "Hay Fever", slug: "hay-fever" },
            { name: "Heart Disease", slug: "heart-disease" },
            { name: "High Blood Pressure", slug: "high-blood-pressure" },
            { name: "High Cholestrol", slug: "high-cholestrol" },
            { name: "Hives", slug: "hives" },
            { name: "Liver Disease", slug: "liver-disease" },
            { name: "Migraine", slug: "migraine" },
            { name: "Pneumococcal Vaccine", slug: "pneumococcal-vaccine" },
            { name: "Sinus", slug: "sinus" },
            { name: "Stroke", slug: "stroke" },
            { name: "Thyroid Disease", slug: "thyroid-disease" },
            { name: "Tuberculosis", slug: "tuberculosis" }
          ];

          const normalizeRelationship = (rel: string): string | null => {
            const r = rel.toLowerCase();
            if (r.includes("grand")) return "Grandparents";
            if (r.includes("father")) return "Father";
            if (r.includes("mother")) return "Mother";
            if (r.includes("brother")) return "Brother(s)";
            if (r.includes("sister")) return "Sister(s)";
            if (r.includes("daughter")) return "Daughter(s)";
            if (r.includes("son")) return "Son(s)";
            return null;
          };

          const findMasterSlug = (diseaseName: string): string | null => {
            const nameLower = diseaseName.toLowerCase().trim();
            // Try exact or partial match first
            const match = masterDiseases.find(m => {
              const mName = m.name.toLowerCase();
              return mName === nameLower || mName.includes(nameLower) || nameLower.includes(mName);
            });
            if (match) return match.slug;

            // Check synonyms
            if (nameLower.includes("hypertension") || nameLower.includes("high bp") || nameLower.includes("bp")) {
              const m = masterDiseases.find(m => m.slug === "high-blood-pressure");
              if (m) return m.slug;
            }
            if (nameLower.includes("cholesterol") || nameLower.includes("lipid") || nameLower.includes("cholestrol")) {
              const m = masterDiseases.find(m => m.slug === "high-cholestrol");
              if (m) return m.slug;
            }
            if (nameLower.includes("depression") || nameLower.includes("anxiety") || nameLower.includes("psychiatric") || nameLower.includes("mental") || nameLower.includes("illness")) {
              const m = masterDiseases.find(m => m.slug === "anxietydepression-or-psychiatric-illness");
              if (m) return m.slug;
            }
            if (nameLower.includes("alcohol") || nameLower.includes("drug") || nameLower.includes("substance") || nameLower.includes("abuse")) {
              const m = masterDiseases.find(m => m.slug === "alcohol-or-drug-abuse");
              if (m) return m.slug;
            }
            if (nameLower.includes("heart") || nameLower.includes("coronary") || nameLower.includes("cardiac")) {
              const m = masterDiseases.find(m => m.slug === "heart-disease");
              if (m) return m.slug;
            }
            if (nameLower.includes("tuberculosis") || nameLower.includes("tb")) {
              const m = masterDiseases.find(m => m.slug === "tuberculosis");
              if (m) return m.slug;
            }
            return null;
          };

          // Normalize to a flat array of raw condition strings
          let rawConditions: string[] = [];
          if (Array.isArray(familyHistoryInput)) {
            rawConditions = familyHistoryInput.map(s => String(s || "").trim()).filter(Boolean);
          } else if (typeof familyHistoryInput === "string" && familyHistoryInput.toLowerCase() !== "none" && familyHistoryInput.trim() !== "") {
            rawConditions = [familyHistoryInput];
          }

          const conditions: string[] = [];
          for (const item of rawConditions) {
            const splitItems = item.split(/[,;\n\.]/).map((s: string) => s.trim()).filter((s: string) => s.length > 0);
            conditions.push(...splitItems);
          }

          for (const condition of conditions) {
            const familyMemberRegex = /(father|mother|brother|sister|grandfather|grandmother|grandparent|aunt|uncle|son|daughter)/gi;
            const matches = Array.from(condition.matchAll(familyMemberRegex));
            if (matches.length === 0) continue;

            // Extract the disease name by removing all family relationship words and common stop words
            const rawDisease = condition
              .replace(familyMemberRegex, '')
              .replace(/\b(and|or|in|has|have|had|'s|with|history of)\b/gi, '')
              .trim() || condition;

            const cleanedDisease = rawDisease
              .replace(/[^\w\s,-]/g, '') // strip punctuation like parentheses, colons, etc.
              .replace(/^[\s,-]+|[\s,-]+$/g, '') // trim leading/trailing space/comma/hyphen
              .replace(/\s+/g, ' ') // collapse multiple spaces
              .trim();

            const diseaseSlug = findMasterSlug(cleanedDisease);
            if (!diseaseSlug) {
              console.warn(`Could not find a matching family history disease master for condition: "${cleanedDisease}" (raw: "${condition}")`);
              continue;
            }

            // Save the condition for each relationship found
            for (const match of matches) {
              const rawRelationship = match[0];
              const normalizedRel = normalizeRelationship(rawRelationship);
              if (!normalizedRel) continue;

              const familyPayload = {
                slug: diseaseSlug,
                patient_id: String(activePatientId),
                details: normalizedRel,
                status: false // Laravel expects status: false to insert details into the database array
              };
              const url = `${BASE_URL}/resource/familyHistories`;
              await apiHandler(token, "POST", familyPayload, url);
            }
          }
        } catch (e) {
          console.error("Error saving family history:", e);
        }
      }

      // 6. Save prescriptions plan details
      if (activeSoap.plan?.medications && activeSoap.plan.medications.length > 0) {
        try {
          const medValues = activeSoap.plan.medications.map((med: string) => ({
            medicine_name: med,
            medicine_type: "Tablet",
            medicine_input: "1",
            medicine_taken: { m: "1", a: "0", e: "0", n: "1" },
            medicine_takenat: "AF",
            total_qty: 10,
            total_medicine_duration: 5,
            medicine_duration_type: "Days",
            dosage: "500mg",
            additional_info: {
              start_date: currentDate,
              is_Active: true,
              end_date: currentDate,
              route: "Oral"
            }
          }));

          const prescriptionPayload = {
            patient_id: activePatientId,
            slug: "prescription",
            values: medValues
          };
          const historiesUrl = `${BASE_URL}/resource/patientHistories`;
          await apiHandler(token, "POST", prescriptionPayload, historiesUrl);
        } catch (e) {
          console.error("Error saving medications plan record:", e);
        }
      }

      // 7. Save Lab Orders
      if (activeSoap.plan?.lab_orders && activeSoap.plan.lab_orders.length > 0) {
        for (const lab of activeSoap.plan.lab_orders) {
          try {
            const labPayload = {
              slug: 'lab',
              user_id: activePatientId,
              addition_info: { title: lab, notes: 'AI Generated order', date: currentDate }
            };
            await apiHandler(token, "POST", labPayload, `${BASE_URL}/resource/docs`);
          } catch (e) { console.error("Error saving lab order:", e); }
        }
      }

      // 8. Save Imaging Orders
      if (activeSoap.plan?.imaging_orders && activeSoap.plan.imaging_orders.length > 0) {
        for (const img of activeSoap.plan.imaging_orders) {
          try {
            const imgPayload = {
              slug: 'imaging',
              user_id: activePatientId,
              addition_info: { title: img, notes: 'AI Generated order', date: currentDate }
            };
            await apiHandler(token, "POST", imgPayload, `${BASE_URL}/resource/docs`);
          } catch (e) { console.error("Error saving imaging order:", e); }
        }
      }



      // Dispatch event to trigger refresh in active parent forms
      window.dispatchEvent(new CustomEvent("refresh-soap-notes"));
      
      setBgSaveMessage("SOAP notes successfully saved to patient chart!");
      setBgSaveSeverity("success");
      setTimeout(() => {
        setBgSaving(false);
      }, 3000);

    } catch (err: any) {
      console.error("Error saving SOAP notes:", err);
      setBgSaving(true);
      setBgSaveMessage("Failed to save SOAP notes to patient chart.");
      setBgSaveSeverity("error");
      setTimeout(() => {
        setBgSaving(false);
      }, 4000);
    } finally {
      setSavingSoap(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    alert(`Copied ${label} to clipboard!`);
  };

  const handleFieldChange = (sectionId: string, key: string, val: any) => {
    setEditableSoap((prev: any) => {
      if (!prev) return prev;
      const updatedSection = { ...prev[sectionId], [key]: val };
      return { ...prev, [sectionId]: updatedSection };
    });
  };

  const renderEditableSection = (sectionId: string) => {
    if (!editableSoap || !editableSoap[sectionId]) return <p className="text-slate-400 text-xs">No information extracted.</p>;
    const sectionData = editableSoap[sectionId];
    
    if (typeof sectionData !== "object" || Object.keys(sectionData).length === 0) {
      return <p className="text-slate-400 text-xs">No fields to edit.</p>;
    }

    return (
      <div className="space-y-4">
        {Object.entries(sectionData).map(([key, val]: [string, any]) => {
          const isNestedObject = typeof val === "object" && val !== null;
          
          if (isNestedObject) {
            return (
              <div key={key} className="space-y-2 border border-slate-100 p-3.5 rounded-2xl bg-slate-50/50">
                <span className="font-extrabold capitalize text-slate-800 text-[10px] tracking-wider uppercase block">
                  {key.replace(/_/g, " ")}
                </span>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(val).map(([subKey, subVal]: [string, any]) => (
                    <div key={subKey} className="space-y-1">
                      <span className="font-extrabold capitalize text-slate-500 text-[9px] block">
                        {subKey.replace(/_/g, " ")}
                      </span>
                      <input
                        type="text"
                        value={String(subVal || "")}
                        onChange={(e) => {
                          const updatedSub = { ...val, [subKey]: e.target.value };
                          handleFieldChange(sectionId, key, updatedSub);
                        }}
                        className="w-full h-9 px-3 rounded-xl border border-slate-200 text-slate-800 text-xs font-semibold bg-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          return (
            <div key={key} className="space-y-1.5">
              <span className="font-extrabold capitalize text-slate-700 text-[10px] tracking-wider uppercase block ml-1">
                {key.replace(/_/g, " ")}
              </span>
              <textarea
                rows={2}
                value={String(val || "")}
                onChange={(e) => handleFieldChange(sectionId, key, e.target.value)}
                className="w-full px-4 py-3 rounded-2xl border border-slate-200 text-slate-800 text-xs font-semibold bg-slate-50/30 focus:bg-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all resize-y"
              />
            </div>
          );
        })}
      </div>
    );
  };

  if (!mounted) return null;

  return createPortal(
    <>
      {/* Floating Sparkle Toggle Button */}
      {!isCallActive && !hideGlobalTrigger && (
        <motion.button
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            // General floating toggle clears patient context
            setSelectedPatientId(null);
            setSelectedPatientName(null);
            setIsOpen(true);
          }}
          className="fixed bottom-6 right-6 z-[9999] bg-gradient-to-r from-violet-600 via-indigo-600 to-blue-600 text-white px-5 py-3.5 rounded-full shadow-2xl flex items-center gap-2.5 font-black text-sm tracking-wide border border-white/20 hover:shadow-indigo-500/20 transition-all duration-300"
        >
          <Sparkles size={18} className="animate-pulse" />
          <span>Ambient AI™</span>
        </motion.button>
      )}

      {/* Slide-over Side Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              key="ambient-drawer"
              initial={{ x: "100%", opacity: 1 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 1 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className={`fixed right-0 top-0 bottom-0 h-screen bg-white flex flex-col z-[9995] border-l border-slate-200 pointer-events-auto transition-all duration-300 w-full ${
                isCallActive ? "max-w-[450px]" : "max-w-2xl"
              }`}
            >
              {/* Header */}
              <div className="bg-slate-900 p-6 text-white relative overflow-hidden flex items-center justify-between">
                {/* Sparkle Wave Background */}
                <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
                
                <div className="flex items-center gap-3 relative z-10">
                  <div className="p-2.5 bg-indigo-500/20 border border-indigo-400/20 rounded-2xl flex items-center justify-center">
                    <Sparkles className="text-indigo-400 animate-pulse" size={20} />
                  </div>
                  <div>
                    <h2 className="text-lg font-black tracking-tight flex items-center gap-1.5 leading-none mb-1">
                      Telegaruda Ambient AI™
                    </h2>
                    {selectedPatientName ? (
                      <p className="text-emerald-400 text-[10px] font-black uppercase tracking-widest leading-none mt-1">
                        CONSULTING: {selectedPatientName}
                      </p>
                    ) : (
                      <p className="text-white/50 text-[10px] font-bold uppercase tracking-widest leading-none mt-1">
                        Voice Consultation Assistant
                      </p>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => setIsOpen(false)}
                  className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all relative z-10 border border-white/5"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Scrollable Work Area */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                
                {/* Audio Recording & Uploader Portal */}
                {!result && !loading && (
                  <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm space-y-6">
                    <div className="text-center max-w-md mx-auto space-y-2">
                      <h3 className="text-base font-extrabold text-slate-800">
                        Record or Upload Consultation
                      </h3>
                      <p className="text-slate-400 text-xs font-medium">
                        Ambient AI listens to standard patient discussions to construct structured medical SOAP forms, extract vitals, and generate clinical insights instantly.
                      </p>
                    </div>

                    {/* Microphone Panel */}
                    <div className="flex flex-col items-center justify-center py-6 border border-dashed border-slate-200 rounded-3xl bg-slate-50/50 relative overflow-hidden">
                      {isRecording && (
                        <div className="absolute inset-0 bg-rose-500/5 flex items-center justify-center pointer-events-none">
                          {/* Live Pulse waves */}
                          <div className="w-40 h-40 rounded-full border border-rose-500/20 animate-ping absolute" />
                          <div className="w-24 h-24 rounded-full border border-rose-500/30 animate-pulse absolute" />
                        </div>
                      )}

                      <div className="relative z-10 flex flex-col items-center space-y-4">
                        {isRecording ? (
                          <>
                            <button
                              type="button"
                              onClick={stopRecording}
                              className="w-16 h-16 rounded-full bg-rose-600 hover:bg-rose-700 text-white flex items-center justify-center shadow-lg shadow-rose-500/20 hover:scale-105 active:scale-95 transition-all"
                            >
                              <Square size={24} className="fill-white" />
                            </button>
                            <div className="text-center">
                              <span className="text-rose-600 font-black text-sm animate-pulse tracking-wide flex items-center gap-1.5">
                                <span className="w-2.5 h-2.5 rounded-full bg-rose-600 animate-ping" />
                                LIVE CONSULTATION LISTENING...
                              </span>
                              <p className={`font-extrabold text-xl mt-1 tracking-tight transition-colors duration-300 ${recordingTime >= 540 ? "text-rose-600 animate-pulse" : "text-slate-900"}`}>
                                {formatTime(recordingTime)} <span className="text-slate-400 text-xs font-normal">/ 10:00</span>
                              </p>
                              {recordingTime >= 540 && (
                                <span className="text-[10px] text-rose-500 font-bold animate-pulse block mt-1">
                                  Approaching 10-minute limit
                                </span>
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={startRecording}
                              className="w-16 h-16 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white flex items-center justify-center shadow-lg shadow-indigo-500/20 hover:scale-105 active:scale-95 transition-all"
                            >
                              <Mic size={24} />
                            </button>
                            <div className="text-center">
                              <span className="text-slate-800 font-black text-xs uppercase tracking-widest">
                                Live Mic Consultation
                              </span>
                              <p className="text-slate-400 text-[10px] font-bold mt-1">
                                Click to record live consultation audio (Max 10 mins)
                              </p>
                            </div>
                          </>
                        )}
                      </div>


                    </div>

                    <div className="flex items-center justify-between text-slate-300 px-4">
                      <hr className="w-1/3 border-slate-200" />
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                        OR UPLOAD FILE
                      </span>
                      <hr className="w-1/3 border-slate-200" />
                    </div>

                    {/* File Dropzone */}
                    <div className="relative group cursor-pointer border border-dashed border-slate-200 hover:border-indigo-400/50 rounded-3xl p-6 text-center bg-white hover:bg-slate-50/50 transition-all duration-300">
                      <input
                        type="file"
                        accept="audio/*"
                        onChange={handleFileChange}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                      />
                      <div className="flex flex-col items-center space-y-2">
                        <div className="p-3 bg-slate-50 rounded-2xl group-hover:bg-white transition-colors border border-slate-100">
                          <Upload size={20} className="text-slate-400 group-hover:text-indigo-600 transition-colors" />
                        </div>
                        <p className="text-xs font-extrabold text-slate-700">
                          Browse Audio Recording File
                        </p>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                          Supports MP3, WAV, M4A, WEBM
                        </p>
                      </div>
                    </div>

                    {/* Selected File Card */}
                    {audioFile && (
                      <div className="p-4.5 rounded-2xl bg-indigo-50/50 border border-indigo-100 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 bg-indigo-600 text-white rounded-xl">
                            <FileAudio size={18} />
                          </div>
                          <div>
                            <p className="text-xs font-black text-slate-800 truncate max-w-sm">
                              {audioFile.name}
                            </p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                              {(audioFile.size / (1024 * 1024)).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleReset}
                          className="w-8 h-8 rounded-xl bg-white border border-slate-200 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-colors shadow-sm"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}

                    {/* Audio Player Preview */}
                    {audioUrl && (
                      <div className="pt-2">
                        <audio src={audioUrl} controls className="w-full" />
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="button"
                      disabled={!audioFile}
                      onClick={handleSubmit}
                      className="w-full h-12.5 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white font-black text-sm shadow-lg shadow-indigo-500/20 hover:scale-[1.01] active:scale-[0.99] transition-all flex items-center justify-center gap-2.5 disabled:opacity-50"
                    >
                      <Sparkles size={16} />
                      <span>GENERATE CLINICAL SOAP NOTES</span>
                    </button>
                  </div>
                )}

                {/* Error Box */}
                {error && (
                  <div className="p-4.5 rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-bold leading-relaxed flex items-start gap-3">
                    <AlertCircle size={16} className="shrink-0 mt-0.5" />
                    <div>{error}</div>
                  </div>
                )}

                {/* Pulsating Waveform Processing State */}
                {loading && (
                  <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col items-center justify-center py-16 text-center space-y-6">
                    <div className="flex items-center gap-1.5 h-16 justify-center">
                      {[...Array(8)].map((_, i) => (
                        <motion.div
                          key={i}
                          animate={{
                            height: [12, i % 2 === 0 ? 48 : 36, 12],
                          }}
                          transition={{
                            duration: 0.8,
                            repeat: Infinity,
                            delay: i * 0.1,
                          }}
                          className="w-1.5 bg-gradient-to-t from-violet-600 to-indigo-500 rounded-full"
                        />
                      ))}
                    </div>
                    <div className="space-y-1.5 max-w-sm">
                      <h3 className="text-base font-black text-slate-800 tracking-tight flex items-center justify-center gap-2">
                        <Loader2 className="animate-spin text-indigo-600" size={16} />
                        Processing Ambient Consultation...
                      </h3>
                      <p className="text-slate-400 text-xs font-semibold">
                        Telegaruda Ambient Engine is listening to the discussion, generating summaries, mapping SOAP structures, and sorting billing codes.
                      </p>
                    </div>
                  </div>
                )}

                {/* Response / Extraction Viewer */}
                {result && (
                  <div className="space-y-6">
                    {/* Navigation Tabs */}
                    <div className="flex bg-slate-200/50 p-1.5 rounded-2xl border border-slate-200/30 gap-1.5">
                      {(["soap", "transcript", "insights"] as const).map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setActiveTab(tab)}
                          className={`flex-1 py-3.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all duration-300 ${
                            activeTab === tab
                              ? "bg-white text-slate-900 shadow-sm border border-slate-100"
                              : "text-slate-500 hover:text-slate-800"
                          }`}
                        >
                          {tab === "soap" ? "Structured SOAP" : tab === "transcript" ? "Conversations" : "Insights & Codes"}
                        </button>
                      ))}
                    </div>

                    {/* Tab 1: SOAP Structure */}
                    {activeTab === "soap" && (
                      <div className="space-y-4">
                        {[
                          { id: "subjective", title: "Subjective Notes", data: result.soap.subjective },
                          { id: "patient_history", title: "Patient History", data: result.soap.patient_history },
                          { id: "objective", title: "Objective (Vitals & Exams)", data: result.soap.objective },
                          { id: "assessment", title: "Assessment Details", data: result.soap.assessment },
                          { id: "plan", title: "Plan & Recommendations", data: result.soap.plan },
                          { id: "ros", title: "Review of Systems (ROS)", data: result.soap.ros },
                          { id: "follow_up", title: "Follow Up", data: result.soap.follow_up }
                        ].map((sec) => (
                          <div
                            key={sec.id}
                            className="bg-white rounded-[1.5rem] border border-slate-100 shadow-sm overflow-hidden"
                          >
                            <button
                              type="button"
                              onClick={() => setExpandedSection(expandedSection === sec.id ? null : sec.id)}
                              className="w-full p-5 flex items-center justify-between hover:bg-slate-50/50 transition-colors text-left"
                            >
                              <span className="font-extrabold text-slate-800 text-sm tracking-tight">
                                {sec.title}
                              </span>
                              <ChevronDown
                                size={16}
                                className={`text-slate-400 transition-transform duration-300 ${
                                  expandedSection === sec.id ? "rotate-180" : ""
                                }`}
                              />
                            </button>

                            <AnimatePresence>
                              {expandedSection === sec.id && (
                                <motion.div
                                  initial={{ height: 0 }}
                                  animate={{ height: "auto" }}
                                  exit={{ height: 0 }}
                                  className="overflow-hidden"
                                >
                                  <div className="px-5 pb-5 pt-1 border-t border-slate-50 bg-white">
                                    {renderEditableSection(sec.id)}
                                    <div className="mt-4 pt-3 border-t border-slate-100 flex justify-end">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const currentVal = editableSoap ? editableSoap[sec.id] : sec.data;
                                          copyToClipboard(
                                            typeof currentVal === "object"
                                              ? JSON.stringify(currentVal, null, 2)
                                              : String(currentVal),
                                            sec.title
                                          );
                                        }}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 hover:border-indigo-400 text-[10px] font-black text-slate-500 hover:text-indigo-600 uppercase tracking-widest transition-colors shadow-sm bg-white"
                                      >
                                        <ClipboardCopy size={12} />
                                        Copy Section
                                      </button>
                                    </div>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        ))}

                        {/* Save SOAP to Chart Actions */}
                        <div className="pt-2">
                          <button
                            type="button"
                            onClick={handleSaveSoapToChart}
                            disabled={savingSoap}
                            className={`w-full py-4 px-6 rounded-2xl font-black text-xs uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-2.5 shadow-md ${
                              saveSuccess
                                ? "bg-emerald-600 text-white shadow-emerald-200/50"
                                : "bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white shadow-indigo-100/50 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
                            }`}
                          >
                            {savingSoap ? (
                              <>
                                <Loader2 size={16} className="animate-spin" />
                                Saving to Patient Chart...
                              </>
                            ) : saveSuccess ? (
                              <>
                                <CheckCircle2 size={16} />
                                Saved to Patient Chart!
                              </>
                            ) : (
                              <>
                                <Upload size={16} />
                                Save SOAP Note to Patient Chart
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Tab 2: Clean Conversations */}
                    {activeTab === "transcript" && (
                      <div className="space-y-4">
                        {/* Clean Conversation dialogue */}
                        <div className="bg-white p-5 rounded-[1.5rem] border border-slate-100 shadow-sm space-y-4">
                          <h4 className="font-extrabold text-slate-800 text-xs uppercase tracking-widest border-b border-slate-50 pb-3 flex items-center justify-between">
                            <span>Clinical Conversation Dialogue</span>
                            <button
                              type="button"
                              onClick={() => copyToClipboard(result.clean_conversation, "Dialogue")}
                              className="flex items-center gap-1.5 text-[9px] text-slate-400 hover:text-indigo-600 tracking-wider transition-colors"
                            >
                              <ClipboardCopy size={10} /> COPY
                            </button>
                          </h4>
                          <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
                            {result.clean_conversation.split("\n").map((line, idx) => {
                              const isDoc = line.trim().startsWith("Doctor:");
                              return (
                                <div key={idx} className="space-y-1">
                                  <span
                                    className={`text-[10px] font-black uppercase tracking-wider ${
                                      isDoc ? "text-indigo-600" : "text-emerald-600"
                                    }`}
                                  >
                                    {isDoc ? "🩺 Doctor" : "👤 Patient"}
                                  </span>
                                  <p className="text-xs text-slate-600 bg-slate-50/50 p-2.5 rounded-2xl border border-slate-100">
                                    {line.replace(/^(Doctor:|Patient:)/, "").trim()}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Raw Podcast/Voice Transcript */}
                        <div className="bg-white p-5 rounded-[1.5rem] border border-slate-100 shadow-sm space-y-4">
                          <h4 className="font-extrabold text-slate-800 text-xs uppercase tracking-widest border-b border-slate-50 pb-3 flex items-center justify-between">
                            <span>Raw Voice Transcript</span>
                            <button
                              type="button"
                              onClick={() => copyToClipboard(result.transcript, "Raw Transcript")}
                              className="flex items-center gap-1.5 text-[9px] text-slate-400 hover:text-indigo-600 tracking-wider transition-colors"
                            >
                              <ClipboardCopy size={10} /> COPY
                            </button>
                          </h4>
                          <p className="text-xs text-slate-500 leading-relaxed max-h-[200px] overflow-y-auto pr-2">
                            {result.transcript}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Tab 3: Insights & Codes */}
                    {activeTab === "insights" && (
                      <div className="space-y-4">
                        {/* Clinical Problem */}
                        {result.insights?.problem && (
                          <div className="bg-white p-5 rounded-[1.5rem] border border-slate-100 shadow-sm space-y-3">
                            <h4 className="font-extrabold text-slate-800 text-xs uppercase tracking-widest flex items-center gap-1.5 text-indigo-600">
                              <Activity size={14} /> Core Diagnostic Problem
                            </h4>
                            <p className="text-sm font-bold text-slate-800 bg-slate-50 p-4 border border-slate-100 rounded-2xl">
                              {typeof result.insights.problem === 'object' && result.insights.problem !== null ? ((result.insights.problem as any).name || (result.insights.problem as any).problem || (result.insights.problem as any).title || Object.values(result.insights.problem).find(v => typeof v === 'string') || 'Unknown') : String(result.insights.problem)}
                            </p>
                          </div>
                        )}

                        {/* Extracted Symptoms */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-white p-5 rounded-[1.5rem] border border-slate-100 shadow-sm space-y-3">
                            <h4 className="font-extrabold text-slate-800 text-xs uppercase tracking-widest flex items-center gap-1.5 text-orange-500">
                              <Heart size={14} /> Symptoms
                            </h4>
                            <div className="flex flex-wrap gap-1.5">
                                {result.soap.extracted_symptoms && result.soap.extracted_symptoms.length > 0 ? (
                                  result.soap.extracted_symptoms.map((s: any, i: number) => (
                                    <span
                                      key={i}
                                      className="px-2.5 py-1 bg-orange-50 border border-orange-100 text-orange-700 rounded-lg text-[10px] font-bold"
                                    >
                                      {typeof s === 'object' && s !== null ? (s.name || s.symptom || s.title || Object.values(s).find(v => typeof v === 'string') || 'Unknown') : String(s)}
                                    </span>
                                  ))
                              ) : (
                                <span className="text-xs text-slate-400">No symptoms extracted.</span>
                              )}
                            </div>
                          </div>

                          {/* Extracted Diagnoses */}
                          <div className="bg-white p-5 rounded-[1.5rem] border border-slate-100 shadow-sm space-y-3">
                            <h4 className="font-extrabold text-slate-800 text-xs uppercase tracking-widest flex items-center gap-1.5 text-rose-500">
                              <Calendar size={14} /> Diagnoses
                            </h4>
                            <div className="flex flex-wrap gap-1.5">
                              {result.soap.extracted_diagnosis && result.soap.extracted_diagnosis.length > 0 ? (
                                result.soap.extracted_diagnosis.map((d: any, i: number) => (
                                  <span
                                    key={i}
                                    className="px-2.5 py-1 bg-rose-50 border border-rose-100 text-rose-700 rounded-lg text-[10px] font-bold"
                                  >
                                    {typeof d === 'object' && d !== null ? (d.name || d.diagnosis || d.title || Object.values(d).find(v => typeof v === 'string') || 'Unknown') : String(d)}
                                  </span>
                                ))
                              ) : (
                                <span className="text-xs text-slate-400">No diagnoses extracted.</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Reset button to record another consult */}
                        <div className="pt-2">
                          <button
                            type="button"
                            onClick={handleReset}
                            className="w-full h-11.5 rounded-2xl border border-slate-200 text-slate-500 font-bold hover:bg-slate-50 transition-colors text-xs uppercase tracking-widest flex items-center justify-center gap-1.5"
                          >
                            <Trash2 size={12} />
                            Record / Upload Another Consultation
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <SnackbarMessage
        open={bgSaving}
        message={bgSaveMessage}
        severity={bgSaveSeverity}
        onClose={() => setBgSaving(false)}
      />
    </>,
    document.body
  );
};
